import { useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '../../../shared/modal';
import {
    AgentType,
    IUserRow,
    IUserWidgetOptions,
    useGetUsersListQuery,
    useUpdateUserWidgetOptionsMutation,
} from '../../../store/users/usersApi';

const AGENTS: AgentType[] = ['leo', 'claire', 'rebecca'];

type UserWidgetDetailsForm = IUserWidgetOptions & { agent: AgentType };

const initialOptions: UserWidgetDetailsForm = {
    agent: 'leo',
    showWidgetForClients: false,
    showWidgetForUsers: false,
};

const agentBadgeClassMap: Record<AgentType, string> = {
    leo: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700',
    claire: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700',
    rebecca: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700',
};

const resolveDefaultAgent = (user: IUserRow): AgentType => {
    if (user.agent) return user.agent;
    const fromPerms = Object.keys(user.widgetPermissions || {}).find((k) =>
        AGENTS.includes(k as AgentType),
    ) as AgentType | undefined;
    if (fromPerms) return fromPerms;
    return 'leo';
};

const UsersPage = () => {
    const { data: users = [], isLoading: usersLoading } = useGetUsersListQuery({
        page: 1,
        limit: 10,
    });

    const [selectedUser, setSelectedUser] = useState<IUserRow | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const { register, handleSubmit, reset, setValue, watch } = useForm<UserWidgetDetailsForm>({
        defaultValues: initialOptions,
    });
    const selectedAgentInForm = watch('agent');
    const [updateUserWidgetOptions, { isLoading: savingOptions }] = useUpdateUserWidgetOptionsMutation();

    const getDefaultOptionsFromUser = (user: IUserRow, agentKey: AgentType): UserWidgetDetailsForm => {
        const permissionFromAgent = user.widgetPermissions?.[agentKey];
        return {
            agent: agentKey,
            showWidgetForClients: Boolean(permissionFromAgent?.personalWidgetEnabled),
            showWidgetForUsers: Boolean(permissionFromAgent?.agentWidgetEnabled),
        };
    };

    const onOpenDetails = (user: IUserRow) => {
        setSaveError(null);
        setSelectedUser(user);
        const agentKey = resolveDefaultAgent(user);
        reset(getDefaultOptionsFromUser(user, agentKey));
    };

    const onCloseDetails = () => {
        setSaveError(null);
        setSelectedUser(null);
        reset(initialOptions);
    };

    const onAgentSelect = (user: IUserRow, agentKey: AgentType) => {
        setValue('agent', agentKey);
        const perm = user.widgetPermissions?.[agentKey];
        setValue('showWidgetForClients', Boolean(perm?.personalWidgetEnabled));
        setValue('showWidgetForUsers', Boolean(perm?.agentWidgetEnabled));
    };

    const onSaveOptions = async (formValues: UserWidgetDetailsForm) => {
        if (!selectedUser) return;
        setSaveError(null);

        if (!selectedUser.clientId) {
            setSaveError('Client id is missing for this user.');
            return;
        }

        try {
            await updateUserWidgetOptions({
                agent: formValues.agent,
                clientId: selectedUser.clientId,
                personalWidgetEnabled: formValues.showWidgetForClients,
                agentWidgetEnabled: formValues.showWidgetForUsers,
            }).unwrap();
            onCloseDetails();
        } catch (error: any) {
            const message =
                error?.data?.message ||
                error?.error ||
                'Unable to save widget settings. Please try again.';
            setSaveError(message);
        }
    };

    return (
        <div>
            <h1 className='text-xl md:text-2xl font-bold text-[#101010] dark:text-primary-darkMode'>
                Users
            </h1>
            <p className='mt-2 text-sm text-gray-600 dark:text-gray-400'>
                Manage user widget visibility settings.
            </p>

            <div className='mt-5 overflow-x-auto border border-1 rounded-xl dark:border-borderColor-darkMode'>
                <table className='min-w-full bg-white dark:bg-black dark:text-white'>
                    <thead className='text-left text-sm font-medium text-black dark:text-white border-b'>
                        <tr>
                            <th className='px-4 py-3 min-w-[220px]'>Name</th>
                            <th className='px-4 py-3 min-w-[220px]'>Email</th>
                            <th className='px-4 py-3 min-w-[180px]'>Username</th>
                            <th className='px-4 py-3 min-w-[140px]'>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {usersLoading &&
                            Array.from({ length: 8 }).map((_, i) => (
                                <tr key={`users-skeleton-${i}`} className='animate-pulse border-b'>
                                    {Array.from({ length: 5 }).map((__, j) => (
                                        <td key={`users-skeleton-cell-${i}-${j}`} className='px-4 py-4'>
                                            <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4' />
                                        </td>
                                    ))}
                                </tr>
                            ))}

                        {!usersLoading && users.length === 0 && (
                            <tr>
                                <td colSpan={5} className='px-4 py-8 text-center text-gray-500 dark:text-gray-400'>
                                    No users found
                                </td>
                            </tr>
                        )}

                        {!usersLoading &&
                            users.map((user) => (
                                <tr key={user.id} className='hover:bg-gray-50 dark:hover:bg-bgColor-darkMode text-sm border-b'>
                                    <td className='px-4 py-4'>{user.name || '--'}</td>
                                    <td className='px-4 py-4'>{user.email || '--'}</td>
                                    <td className='px-4 py-4'>{user.username || '--'}</td>
                                    <td className='px-4 py-4'>
                                        <button
                                            type='button'
                                            onClick={() => onOpenDetails(user)}
                                            className='text-primary dark:text-primary-darkMode hover:underline font-medium'
                                        >
											<img src="/assets/table-actions/see-more.svg" alt="arrow-right" className='w-4 h-4 rotate-90' />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            <Modal
                open={Boolean(selectedUser)}
                onClose={onCloseDetails}
                title={`User Details${selectedUser ? ` - ${selectedUser.name}` : ''}`}
                contentContainerStyle='w-full max-w-[520px] !max-h-[85vh] overflow-y-auto'
            >
                <form className='pt-2 pb-1' onSubmit={handleSubmit(onSaveOptions)}>
                    <input type='hidden' {...register('agent')} />
                    <div className='mb-5'>
                        <p className='text-sm font-medium text-gray-800 dark:text-gray-200 mb-2'>
                            Agent
                        </p>
                        <p className='text-xs text-gray-500 dark:text-gray-400 mb-3'>
                            Choose which agent these widget settings apply to.
                        </p>
                        <div className='flex flex-wrap gap-2'>
                            {AGENTS.map((agentKey) => {
                                const isSelected = selectedAgentInForm === agentKey;
                                return (
                                    <button
                                        key={agentKey}
                                        type='button'
                                        onClick={() => selectedUser && onAgentSelect(selectedUser, agentKey)}
                                        className={`
                                            inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-medium capitalize transition
                                            ${agentBadgeClassMap[agentKey]}
                                            ${isSelected ? 'ring-2 ring-offset-2 ring-[#00330F] dark:ring-primary-darkMode dark:ring-offset-bgColor-darkMode' : 'opacity-80 hover:opacity-100'}
                                        `}
                                    >
                                        {agentKey}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className='space-y-4'>
                        <label className='flex items-center gap-3 text-sm text-gray-700 dark:text-gray-200 cursor-pointer'>
                            <input
                                type='checkbox'
                                {...register('showWidgetForClients')}
                                className='h-4 w-4'
                            />
                            Show widget for their clients
                        </label>

                        <label className='flex items-center gap-3 text-sm text-gray-700 dark:text-gray-200 cursor-pointer'>
                            <input
                                type='checkbox'
                                {...register('showWidgetForUsers')}
                                className='h-4 w-4'
                            />
                            Show widget for their users
                        </label>
                    </div>
                    {saveError && (
                        <p className='mt-3 text-sm text-red-600 dark:text-red-400'>{saveError}</p>
                    )}

                    <div className='mt-6 flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-bgColor-darkMode py-3'>
                        <button
                            type='button'
                            onClick={onCloseDetails}
                            className='px-4 py-2 rounded-md border border-gray-300 dark:border-gray-700 text-sm'
                        >
                            Cancel
                        </button>
                        <button
                            type='submit'
                            disabled={savingOptions}
                            className='px-4 py-2 rounded-md text-sm text-white bg-primary-default disabled:opacity-60'
                        >
                            {savingOptions ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default UsersPage;
